"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { ArrowUpDown, ShieldCheck, Star, ChevronRight, MapPin, User } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { buildProviderProfilePath } from "@/lib/provider-url";

export type ProviderTableRow = {
  slug: string;
  name: string;
  suburb: string;
  googleRating: number;
  ratingSort?: number;
};

type ProviderTableProps = {
  rows: ProviderTableRow[];
  cityName: string;
  providerCount: number;
};

type SortKey = "name" | "rating";

const SORT_LABELS: Array<[SortKey, string]> = [
  ["name", "Provider Name"],
  ["rating", "Google Rating"],
];

export function ProviderTable({
  rows,
  cityName,
  providerCount,
}: ProviderTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const validRows = useMemo(() => rows.filter((row) => row.name?.trim()), [rows]);
  const [displayCount, setDisplayCount] = useState(10);

  const sortedRows = useMemo(
    () =>
      [...validRows].sort((left, right) => {
        if (sortKey === "name") return left.name.localeCompare(right.name);
        if (sortKey === "rating") {
          return (right.ratingSort ?? right.googleRating) - (left.ratingSort ?? left.googleRating);
        }
        return 0;
      }),
    [validRows, sortKey],
  );

  const visibleRows = sortedRows.slice(0, displayCount);
  const hasMore = displayCount < sortedRows.length;

  if (validRows.length === 0) {
    return (
      <div className="w-full">
        <Card elevation="elevated" className="p-6 md:p-12 border-black/5 bg-white text-center">
          <div className="max-w-md mx-auto">
            <div className="h-14 w-14 mx-auto rounded-2xl bg-slate-50 border border-black/5 flex items-center justify-center text-slate-400 mb-6">
              <ShieldCheck size={24} />
            </div>
            <h3 className="text-xl font-semibold tracking-tight text-slate-900 mb-3">
              No public provider listings for {cityName} yet
            </h3>
            <p className="text-sm font-medium text-slate-500 leading-relaxed mb-6">
              We don&apos;t have public provider listings for this area yet. Search the AHPRA register to find registered practitioners near you.
            </p>
            <a
              href="https://www.ahpra.gov.au/Registration/Registers-of-Practitioners.aspx"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-50 px-6 py-3 text-sm font-bold text-emerald-700 hover:bg-emerald-100 transition-colors"
            >
              Search AHPRA Register
              <ChevronRight size={16} />
            </a>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Card elevation="elevated" className="mb-6 p-6 md:p-8 border-black/5 bg-white/50 backdrop-blur-xl">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {SORT_LABELS.map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setSortKey(value)}
                className={cn(
                  "inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-xs font-bold transition-colors shadow-sm uppercase tracking-widest",
                  sortKey === value
                    ? "border-emerald-700/30 bg-emerald-700 text-white shadow-emerald-700/20"
                    : "border-black/5 bg-white text-slate-500 hover:border-black/10 hover:text-slate-900",
                )}
              >
                <ArrowUpDown
                  size={12}
                  className={cn("transition-opacity", sortKey === value ? "opacity-100" : "opacity-40")}
                />
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-4 border-l border-black/5 pl-6">
            <div className="text-right">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Total Sample</p>
              <p className="text-sm font-bold text-slate-900">
                {validRows.length} Active {validRows.length === 1 ? "Clinic" : "Clinics"}
              </p>
            </div>
          </div>
        </div>
      </Card>

      <ul role="list" className="grid gap-4">
        {visibleRows.map((row) => (
          <li key={row.slug}>
            <Link href={buildProviderProfilePath(row)} className="group block">
              <Card
                elevation="elevated"
                className="p-6 md:p-8 border-black/5 bg-white transition-[transform,box-shadow] duration-300 hover:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.06)] hover:-translate-y-1 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 bottom-0 w-1 bg-transparent group-hover:bg-emerald-700 transition-colors" />

                <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr_auto] lg:items-center">
                  <div className="flex items-start gap-4">
                    <div className="h-14 w-14 shrink-0 rounded-2xl bg-slate-50 border border-black/5 flex items-center justify-center text-slate-500 group-hover:bg-emerald-50 group-hover:text-emerald-700 transition-colors">
                      <ShieldCheck size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold tracking-tight text-slate-900 mb-1 group-hover:text-emerald-700 transition-colors">
                        {row.name}
                      </h3>
                      <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-widest">
                        <MapPin size={12} className="text-slate-400" />
                        {row.suburb}
                        <span className="h-1 w-1 rounded-sm bg-slate-200 mx-1" />
                        {row.googleRating > 0 ? (
                        <div className="flex items-center gap-1">
                          <Star size={12} className="fill-amber-400 text-amber-400" />
                          <span className="text-slate-900 font-bold">{row.googleRating.toFixed(1)}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400 font-medium">No reviews</span>
                      )}
                      </div>
                      <p className="text-xs text-slate-400 font-normal normal-case tracking-normal mt-0.5">Google reviews; not clinical outcomes.</p>
                    </div>
                  </div>

                  <div className="p-4 rounded-2xl bg-slate-50/50 border border-black/5 group-hover:bg-white group-hover:border-emerald-500/10 transition-colors">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Public Listing</p>
                    <p className="text-sm font-bold text-slate-900 leading-relaxed">
                      View profile, location, and pricing context
                    </p>
                  </div>

                  <div className="flex items-center justify-end">
                    <div className="h-12 w-12 rounded-md bg-slate-50 border border-black/5 flex items-center justify-center text-slate-500 group-hover:bg-slate-900 group-hover:text-white group-hover:scale-110 transition-[box-shadow,border-color] shadow-sm">
                      <ChevronRight size={20} />
                    </div>
                  </div>
                </div>
              </Card>
            </Link>
          </li>
        ))}
      </ul>

      {hasMore && (
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => setDisplayCount((prev) => Math.min(prev + 10, sortedRows.length))}
            className="inline-flex items-center justify-center rounded-xl border border-black/5 bg-white px-6 py-3 text-sm font-bold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900"
          >
            Show {Math.min(10, sortedRows.length - displayCount)} more providers
          </button>
        </div>
      )}

      <div className="mt-8 flex flex-col items-center justify-center gap-4 border-t border-black/5 pt-8">
        <div className="flex items-center justify-center gap-4">
          <div className="flex -space-x-3">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-8 w-8 rounded-full border-2 border-white bg-emerald-50 flex items-center justify-center text-emerald-700 shadow-sm relative z-0"
              >
                <User size={14} strokeWidth={3} />
              </div>
            ))}
          </div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">
            Data sourced from <span className="text-slate-900 font-bold">{providerCount} clinical profiles</span>
          </p>
        </div>
      </div>
    </div>
  );
}
